import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, first, take, tap } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import { RiskModel } from '@amfam/policy/models';
import { AnalyticsFacade, EventAnalyticParams } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  Option,
  ValidationService
} from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehicleQuickQuoteContext,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';

@Component({
  selector: 'quote-or-bind',
  templateUrl: './quote-or-bind.component.html',
  styleUrls: ['./quote-or-bind.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class QuoteOrBindComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() cancelEvent: EventEmitter<any> = new EventEmitter();
  private subscription: Subscription = new Subscription();
  public coverageOptions: Option[];
  private vehicleId: string;
  public quoteForm: UntypedFormGroup;
  hasError$ = this.quickQuoteFeatureFacade.vehicleError$;
  errorMessage$ = this.quickQuoteFeatureFacade.vinYearErrorMessage$;
  years$ = this.quickQuoteFeatureFacade.vehicleYears$;
  authorizedToSeeQuickQuote$ = this.roleService.isAuthorized('replacevehiclequote_view');

  displayPolicyGracePeriod = false;
  sameVINErrorMessage: string;
  isSameVin = false;
  exitPrimaryButtonText = this.copyService.getCopy(
    'policy.replaceVehicleExitFlowPrimaryButtonText'
  );

  public userState$ = this.store.select(userQuery.selectUserState);
  public getAdvanceAutoPolicies$ = this.store.select(PolicySelectors.selectAdvanceAutoPolicies);
  public activeAgents$ = this.quickQuoteFeatureFacade.agents$;
  public showExpectationSettings$ = this.store.select(
    PolicySelectors.selectHasAdvPolicyNotNewCarReplaceCoverageAndCarLightTruck(
      this.route.snapshot.paramMap.get('id')
    )
  );
  private policyNumberFromVehicle$ = this.quickQuoteFeatureFacade.policyNumberFromVehicle$;
  public exitFlowModalId = this.modalService.createModalId('exitFlowModalId');
  public exitFlowConfimationText = this.copyService.getCopy(
    'policy.replaceVehicleExitFlowConfimationText'
  );
  private reaplceVehicleText = this.copyService.getCopy('policy.replaceVehicle');
  private replaceVehicleRisks$: Observable<RiskModel[]> =
    this.quickQuoteFeatureFacade.replaceVehicleRisks$;

  constructor(
    private store: Store,
    private copyService: CopyService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private spinner: LoadingSpinnerService,
    private dockingBarService: DockingBarService,
    private modalService: DsModalService,
    public roleService: ImpersonateRolesService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngAfterViewInit(): void {
    this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.QUOTE_OR_BIND);
    this.roleService
      .isAuthorized('replacevehicle_vinyear')
      .pipe(take(1))
      .subscribe(isAuthorized => {
        if (!isAuthorized) {
          this.quoteForm.get('bind').disable();
        }
      });
  }

  ngOnInit() {
    this.dockingBarService.registerHeading('Replace Vehicle');
    this.vehicleId = this.route.snapshot.paramMap.get('id');
    this.buildForm();
    this.setVehicleId();
  }

  buildForm() {
    this.coverageOptions = this.copyService.getCopy('policy.quickQuoteCoverageOptions');
    this.quoteForm = this.formBuilder.group({
      quoteOption: ['', Validators.compose([Validators.required])],
      quote: this.formBuilder.group({
        year: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
        make: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
        model: ['unset', Validators.compose([ValidationService.unsetStringValidator])]
      }),
      bind: this.formBuilder.group({
        year: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
        vin: [
          '',
          Validators.compose([
            ValidationService.vinFormatValidator,
            ValidationService.vinRequiredValidator
          ])
        ]
      })
    });
    this.handleSpinnerOnLoading();
    this.showExpectationSettings$
      .pipe(
        take(1),
        tap(el => el ?? this.quoteForm.get('quoteOption').setValue({ id: 'bind' }))
      )
      .subscribe();
  }

  toggleGracePeriod() {
    this.displayPolicyGracePeriod = true;
  }

  getQuoteDetails() {
    this.spinner.start({
      secondaryMessage: `We're calculating your quote. This may take a moment.`,
      blockActions: true,
      maxWidth: '338'
    });

    if (this.quoteForm?.value?.quoteOption?.id === 'quote') {
      this.quickQuoteFeatureFacade.setContext(ReplaceVehicleQuickQuoteContext.QUICK_QUOTE);
      this.quickQuoteFeatureFacade.getQuote('quickquotes');
      this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.BUTTON_QUICK_QUOTE_NEXT);

      // GA analytics for Quick quote start
      const quickQuoteStartEvent: EventAnalyticParams = {
        event: 'quick_quote_start',
        product_line: 'Auto',
        form_name: 'Quick Quote'
      };
      this.analyticsFacade.trackEvent(quickQuoteStartEvent);
    } else {
      this.quickQuoteFeatureFacade.setContext(ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE);
      this.quickQuoteFeatureFacade.lookupYearVin(
        (<UntypedFormGroup>this.quoteForm.get('bind')).controls['year'].value,
        (<UntypedFormGroup>this.quoteForm.get('bind')).controls['vin'].value
      );
    }
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  cancel() {
    this.policyNumberFromVehicle$
      .pipe(
        filter(policyNumber => !!policyNumber),
        first(),
        tap(policyNumber =>
          this.store.dispatch(
            fromRouterActions.Go({
              path: [`/policies/auto/${policyNumber}`]
            })
          )
        )
      )
      .subscribe();
  }

  setVehicleId() {
    if (this.vehicleId) this.quickQuoteFeatureFacade.setVehicleId(this.vehicleId);
  }

  handleSpinnerOnLoading() {
    this.subscription.add(
      this.quickQuoteFeatureFacade.isVehicleLoading$.subscribe((loading: Boolean) =>
        !loading ? this.spinner.stop() : ''
      )
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.spinner.stop();
  }

  stayFromExitModal() {
    this.dockingBarService.registerHeading(this.reaplceVehicleText);
  }

  navigateToOverview() {
    this.store
      .pipe(select(routerQuery.getRouterCurrentStateUrl), take(1))
      .subscribe((url: string) => {
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['overview']
          })
        );
      });
  }

  exit() {
    this.modalService.open(this.exitFlowModalId);
  }

  continue() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [`/policies/risk-selection/replace-vehicle-quick-quote/${this.vehicleId}/year-vin`]
      })
    );
  }

  sendToAgent() {
    this.spinner.start({ blockActions: true });
    this.quickQuoteFeatureFacade.setOperation(
      ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_SEND_TO_AGENT_BIND
    );
    this.quickQuoteFeatureFacade.sendToAgentReplaceVehicle();
  }

  verifyVin(vin: string) {
    if (vin.length === 17) {
      this.replaceVehicleRisks$.pipe(take(1)).subscribe(vehicle => {
        const matchingVehicle = vehicle.find(vehicleVINRisks => {
          return vehicleVINRisks?.vin === vin.toUpperCase();
        });
        if (matchingVehicle) {
          this.isSameVin = true;
          this.sameVINErrorMessage = this.copyService.get(
            'policy',
            'sameVINReplaceVehicleErrorMessage'
          );
        } else {
          this.isSameVin = false;
          this.sameVINErrorMessage = '';
        }
      });
    }
  }
}
